<template>
  <div v-if="isPartialMonth" class="partial-month-indicator">
    <span class="badge partial-month-badge" @click="toggleTooltip" @mouseenter="showTooltip" @mouseleave="hideTooltip">
      <i class="fa-solid fa-calendar-days me-1"></i>
      Partial Month
      <span class="days-count ms-1">({{ remainingDays }} days)</span>
    </span>
    
    <!-- Tooltip/Popover -->
    <transition name="fade">
      <div v-if="tooltipVisible" class="proration-tooltip">
        <div class="tooltip-header">
          <i class="fa-solid fa-info-circle me-1"></i>
          Prorated Budget
        </div>
        <div class="tooltip-body">
          <p class="mb-2">
            Your budget started mid-month, so this month's calculations use 
            <strong>{{ prorationPercentage }}%</strong> of your full monthly amounts.
          </p>
          <p class="mb-0">
            <i class="fa-solid fa-calendar-check me-1"></i>
            Starting next month, you'll use the full monthly amounts.
          </p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useBudgetData } from '../composables/useBudgetData'

export default {
  name: 'PartialMonthIndicator',
  setup() {
    const budgetData = useBudgetData()
    const tooltipVisible = ref(false)
    
    const remainingDays = computed(() => {
      if (!budgetData.isPartialMonth.value || !budgetData.budgetStartDate.value) {
        return 0
      }
      
      const startDate = new Date(budgetData.budgetStartDate.value)
      const startDay = startDate.getDate()
      const totalDays = budgetData.daysInMonth.value
      
      return totalDays - startDay + 1
    })
    
    const prorationPercentage = computed(() => {
      return Math.round(budgetData.prorationFactor.value * 100)
    })
    
    const showTooltip = () => {
      tooltipVisible.value = true
    }
    
    const hideTooltip = () => {
      tooltipVisible.value = false
    }
    
    const toggleTooltip = () => {
      tooltipVisible.value = !tooltipVisible.value
    }
    
    return {
      isPartialMonth: budgetData.isPartialMonth,
      prorationFactor: budgetData.prorationFactor,
      remainingDays,
      prorationPercentage,
      tooltipVisible,
      showTooltip,
      hideTooltip,
      toggleTooltip
    }
  }
}
</script>

<style scoped>
.partial-month-indicator {
  position: relative;
  display: inline-block;
}

.partial-month-badge {
  background-color: var(--light-primary);
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.partial-month-badge:hover {
  background-color: color-mix(in srgb, var(--light-primary) 85%, black);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.partial-month-badge:active {
  transform: translateY(0);
}

.days-count {
  opacity: 0.9;
  font-size: 0.8rem;
}

/* Tooltip/Popover */
.proration-tooltip {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  background: var(--light-card);
  border: 2px solid var(--light-border);
  border-radius: 8px;
  padding: 0;
  min-width: 280px;
  max-width: 320px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 1050;
  animation: slideDown 0.2s ease;
}

.proration-tooltip::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid var(--light-border);
}

.proration-tooltip::after {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 7px solid var(--light-card);
}

/* Desktop: Show tooltip above */
@media (min-width: 1200px) {
  .proration-tooltip {
    top: auto;
    bottom: calc(100% + 0.5rem);
    animation: slideUp 0.2s ease;
  }

  .proration-tooltip::before {
    top: auto;
    bottom: -8px;
    border-bottom: none;
    border-top: 8px solid var(--light-border);
  }

  .proration-tooltip::after {
    top: auto;
    bottom: -6px;
    border-bottom: none;
    border-top: 7px solid var(--light-card);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.tooltip-header {
  background: var(--nested-container-bg);
  padding: 0.75rem 1rem;
  border-bottom: 2px solid var(--light-border);
  border-radius: 6px 6px 0 0;
  font-weight: 600;
  color: var(--light-text);
  font-size: 0.9rem;
}

.tooltip-body {
  padding: 1rem;
  color: var(--light-text);
  font-size: 0.875rem;
  line-height: 1.5;
}

.tooltip-body p {
  margin: 0;
}

.tooltip-body strong {
  color: var(--light-primary);
  font-weight: 600;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* Dark Mode Support */
:global([data-theme]) .partial-month-badge {
  background-color: #36A2EB;
}

:global([data-theme]) .partial-month-badge:hover {
  background-color: color-mix(in srgb, #36A2EB 85%, black);
}

:global([data-theme]) .proration-tooltip {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .partial-month-badge {
    font-size: 0.8rem;
    padding: 0.4rem 0.6rem;
  }
  
  .days-count {
    font-size: 0.75rem;
  }
  
  .proration-tooltip {
    min-width: 260px;
    max-width: 90vw;
    left: 0;
    transform: translateX(0);
  }
  
  .proration-tooltip::before,
  .proration-tooltip::after {
    left: 2rem;
  }
}
</style>
